import { observable } from 'rewire-core';
import { onExceptionListen, graphqlClient } from '../graphql/GraphQLClient';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import { ISecurityToken, IUser, Tier } from '../DataTypes';
import { Deferred } from '../utility/Deferred';
import { notifications } from '../ui/Notifications';
import gql from 'graphql-tag';
import { DocumentNode } from 'graphql';
import { GQL } from 'rewire-graphql';
import { history } from '../RouterWithHistory';
import { CookieStorage } from './storage';
import Routes from '../Routes';

interface ICustomModule {
  getOptionsActionMenuItems?: (scheduleModel: any, option: unknown) => void;
  dispose(): void;
  initialize(): Promise<void>;
}

const VERSION = '1.0.30';

// change to cookie storage which is more secure. It also allows authentication cookies to be sent from the server seamlessly
const _storage = CookieStorage;

// If you are using LocalStorage or SessionStorage above you must add this back into _initialize()
// You must add this preable to the _initialize() function
// private async _initialize(): Promise<void> {
//  if (!this.sessionId) {
//     const sessionId = CookieStorage.get(KEY_SESSIONID);
//     if (sessionId) {
//       if (_initialized) return _initialized.promise;
//       await this.setupSession(sessionId);
//       CookieStorage.remove(KEY_SESSIONID);
//       return;
//     }
//   }
//   ... rest of code goes here!!
// }

interface ISecurityContext {
  tier: Tier;
  isMobile: boolean;
  isSuperAdmin: boolean;
  securityTokens: { [token: string]: boolean };
  modules?: { [token: string]: boolean };
}

const _tiers = ['none', 'starter', 'professional', 'enterprise'];
function isMinimumTier(usersTier: Tier, accessTier: Tier) {
  return _tiers.indexOf(usersTier) >= _tiers.indexOf(accessTier);
}

const _securityTokens: { [token: string]: ISecurityToken } = {};
const _securityContext: ISecurityContext = {
  tier: 'starter',
  isMobile: false,
  isSuperAdmin: false,
  securityTokens: { security_none: true },
};

function evalSecurityToken(token: string): { access: boolean; reason?: string } {
  if (!token) return { access: false, reason: 'Permission denied, security token does not exist' };
  const securityToken: ISecurityToken = _securityTokens[token];
  if (!securityToken) return { access: false, reason: 'Permission denied, security token does not exist' };
  if (securityToken.token === 'security_none') return { access: true };
  if (!_securityContext) return { access: false, reason: 'Permission denied, session expired' };
  if (!isMinimumTier(_securityContext.tier, securityToken.minimumTier))
    return {
      access: false,
      reason: `Permission denied, you do not have access to this tier. Please contact WorkSight Flow support to upgrade from ${_securityContext.tier} to the ${securityToken.minimumTier} tier`,
    };
  if (securityToken.module && !_securityContext.modules?.[securityToken.module]) {
    return {
      access: false,
      reason: `Permission denied, you do not have access to the ${securityToken.module} module. Please contact WorkSight Flow support to purchase the ${securityToken.module} module.`,
    };
  }
  if (_securityContext.isSuperAdmin) return { access: true };
  if (!_securityContext.securityTokens[token])
    return {
      access: false,
      reason: `Permission denied, please contact your administrator for access to ${securityToken.description.toLocaleLowerCase()}.`,
    };
  return { access: true };
}

const isSessionValidQuery: DocumentNode = gql`
  query {
    isSessionValid
  }
`;

const checkSession = async () => {
  let hasSession = false;
  try {
    if (window.location.hostname === 'localhost') {
      hasSession = true;
      return true;
    }
    const result = await graphqlClient.query(isSessionValidQuery as unknown as GQL);
    hasSession = result?.data?.isSessionValid;
  } catch {
  } finally {
    if (!hasSession) {
      if (authService.sessionId) notifications.error('Session expired. Redirecting to login.');
      authService.clearAuthorization();
      // biome-ignore lint/correctness/noUnsafeFinally: <explanation>
      return false;
    }
    // biome-ignore lint/correctness/noUnsafeFinally: <explanation>
    return true;
  }
};

onExceptionListen(checkSession);

const KEY_SESSIONID: string = 'sessionId';

let _initialized: Deferred<void> | undefined;
const emptyModule = { dispose() {}, async initialize() {} };
let _customModule: ICustomModule | undefined;

export type OAUTH2 = { flow: 'OAUTH2'; redirectUrl: string };
export type AuthFlow = 'UNKNOWN' | 'USER_PASSWORD' | OAUTH2;
export function isOAUTH2Flow(flow: AuthFlow): flow is OAUTH2 {
  return (flow as OAUTH2)?.flow === 'OAUTH2';
}

interface IAuthServiceState {
  isAuthenticated: boolean;
  isLoading: boolean;
  // userInfo?:       IUser;
  sessionId?: string;
}

class AuthService {
  state: IAuthServiceState;
  private _userInfo: IUser | undefined = undefined;
  readonly storage = _storage;

  constructor() {
    if (window.location.pathname.startsWith('/setup-account')) {
      this.sessionId = undefined;
    }

    this.state = observable({
      isAuthenticated: !!this.sessionId,
      isLoading: false,
    });

    requestAnimationFrame(() => this.initialize());
  }

  async getAuthenticationFlowForEmail(email: string): Promise<AuthFlow> {
    try {
      const result = await DataMutations.getAuthenticationFlowFromEmail(email);
      if (result) {
        return { flow: 'OAUTH2', redirectUrl: result };
      }
    } catch (err) {
      return 'UNKNOWN';
    }
    return 'USER_PASSWORD';
  }

  private async initialize(): Promise<void> {
    try {
      this.state.isLoading = true;
      await this._initialize();
    } finally {
      this.state.isLoading = false;
    }
  }

  private async _initialize(): Promise<void> {
    if (_initialized) return _initialized.promise;
    _initialized = new Deferred();
    graphqlClient.bearer = this.sessionId;

    try {
      if (this.sessionId) {
        const returnSchema = `
          id
          name
          avatar
          email
          userSecurity {
            role {
              id
              code
              name
              tags
              fields
              isSuperAdmin
              isSystem
              securityTokens
              workflow
            }
          }
          tier
          location {
            id
            code
            name
            tags
            fields
            version
            timeFormat
            postedDate
            weekStart
            modules
            tz {
              id
            }
            subscriptionCount
            employeeCount
            activeUsers
            activeEmployees
            tier
            planAdministrator {
              id
              name
            }
            subscriptionExpires
          }
        `;

        const securityTokensSchema = `
          token
          description
          minimumTier
          path
          module
        `;

        const [result, tokens] = await Promise.all([
          DataQueries.getSessionInfo(returnSchema),
          DataQueries.getSecurityTokens(securityTokensSchema),
        ]);
        if (!result?.userSecurity) throw new Error('you were unable to successfully login');
        if (result?.location?.version) {
          if (result.location.version !== VERSION)
            notifications.warning(
              `The application ${VERSION} has a new upgraded version ${result.location.version}. Please refresh your browser to upgrade.`,
            );
        }
        _securityContext.isSuperAdmin = result.userSecurity.role?.isSuperAdmin ?? false;
        _securityContext.securityTokens = result.userSecurity.role?.securityTokens ?? { security_none: true };
        _securityContext.tier = result.tier ?? 'starter';
        _securityContext.isMobile = false;
        _securityContext.modules =
          result.location?.modules?.reduce(
            (p, c) => {
              p[c] = true;
              return p;
            },
            {} as Record<string, boolean>,
          ) ?? {};
        this._userInfo = result as IUser;
        Object.assign(
          _securityTokens,
          tokens.reduce(
            (prev, curr) => {
              if (curr?.token) {
                // filter out security tokens that you have not paid for
                if (curr.module && !result.location?.modules?.includes(curr.module)) return prev;
                prev[curr.token] = curr;
              }
              return prev;
            },
            {} as Record<string, unknown>,
          ),
        );

        try {
          _customModule = (await import(`../customs/module-${result.location?.id ?? ''}.ts`))?.default;
          if (_customModule) {
            await _customModule.initialize();
          }
          console.log('Successfully loaded custom module');
        } catch (err) {
          _customModule = undefined;
          console.error(err);
        }
      }
    } catch (err) {
      this.clearAuthorization();
    }
    _initialized.resolve();
    _initialized = undefined;
    return;
  }

  get customModule(): ICustomModule {
    return _customModule ?? emptyModule;
  }

  get isLoading() {
    return this.state.isLoading;
  }

  async getUserInfo() {
    if (this.sessionId && !this._userInfo) await this.initialize();
    return this._userInfo;
  }

  async getSecurityTokens() {
    if (this.sessionId && !this._userInfo) await this.initialize();
    return Array.from(Object.values(_securityTokens));
  }

  set sessionId(sessionId: string | undefined) {
    _storage.save(KEY_SESSIONID, sessionId, { daysUntilExpire: 80 });
  }

  get sessionId(): string | undefined {
    return _storage.get(KEY_SESSIONID) || undefined;
  }

  set loginRedirectUrl(redirectUrl: string | undefined) {
    _storage.save('LOGIN_REDIRECT', redirectUrl, { daysUntilExpire: 80 });
  }

  get loginRedirectUrl(): string {
    return _storage.get('LOGIN_REDIRECT') ?? Routes.Login;
  }

  get isAuthenticated(): boolean {
    // should this be a ping query to check that your auth token is right?
    return this.state.isAuthenticated;
  }

  evalSecurityToken(token: string) {
    const { access, reason } = evalSecurityToken(token);
    if (!access) {
      console.error(reason ?? 'unknown error evaluating security token');
      return false;
    }
    return true;
  }

  private async setupSession(sessionId?: string, redirect?: string): Promise<boolean> {
    if (sessionId) {
      graphqlClient.bearer = sessionId;
      this.sessionId = sessionId;
      if (redirect) this.loginRedirectUrl = redirect;
      (graphqlClient as any).subscriptionClient?.dispose();
      await this.initialize();
      this.state.isAuthenticated = true; // temp
      return true;
    }

    return false;
  }

  async authenticate(email: string, password: string): Promise<boolean> {
    return await this.setupSession(await DataMutations.login(email, password), Routes.Login);
  }

  async switchToLocation(locationId: string, password: string) {
    if (!this.sessionId || !locationId) {
      throw new Error('you cannot switch locations at this time');
    }

    if (await DataMutations.switchToLocation(this.sessionId, locationId, password)) {
      authService.clearAuthorization();
      location.replace(Routes.Login);
      // history.replace(Routes.Login, );
      return true;
    }
    return false;
  }

  clearAuthorization() {
    // this.userInfo              = undefined;
    this.sessionId = undefined;
    this.state.isAuthenticated = false; // temp
    graphqlClient.bearer = undefined;
    Object.assign(_securityTokens, {});
    Object.assign(_securityContext, {
      tier: 'starter',
      isMobile: false,
      isSuperAdmin: false,
      securityTokens: { security_none: true },
    });
  }

  async logout(): Promise<boolean> {
    let loginRedirectUrl: string | undefined;
    if (this.sessionId) {
      loginRedirectUrl = await DataMutations.logout(this.sessionId);
    }

    this.clearAuthorization();
    if (loginRedirectUrl?.startsWith('http')) {
      this.state.isLoading = true;
      window.location.href = loginRedirectUrl;
      return true;
    }
    history.replace(Routes.Login);
    return true;
  }
}

const authService = new AuthService();
export default authService;
